/* eslint-disable react/jsx-one-expression-per-line */
import { useHookstate } from '@hookstate/core'
import React, { useEffect } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import ErrorFallback from '~/components/ErrorFallback'
import { useLang } from '~/hooks/useLang'
import { useTheme } from '~/hooks/useTheme'
import type { IListDeviceTab } from './interfaces/IListDeviceTab'
import { styled } from '@mui/material/styles'
import Badge, { BadgeProps } from '@mui/material/Badge'
import InternalContent from '~/components/Layout/components/InternalContent'
import { Divider, Grid, IconButton, Tab } from '@mui/material'
import { Refresh } from '@mui/icons-material'
import { getDevicesByStatus } from './services'
import useDeviceTableState from './stores/DeviceTableState'
import { toDomain } from './mapers/device'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import DeviceTableUpdateSocket from './components/DeviceTableUpdateSocket'
import DeviceFilters from './components/DeviceFilters'
import type { IListDeviceSocket } from './interfaces/IListDevicesprops'
import NotificationsCentral from '~/components/NotificationsCentral'
import useSystemState from '~/features/System/stores/SystemState'
import ActionsMultipleDeviceButton from './components/ActionsMultipleDeviceButton'
import DialogDisconnectMultipleDevices from './components/DialogDisconnectMultipleDevices'
import DialogTransferMultipleDevices from './components/DialogTransferMultipleVehicles'
import useListDevicesSocket from './stores/ListDevicesSocketState'

interface ITabListDevice {
  readonly fullList?: boolean
}

// eslint-disable-next-line max-lines-per-function
export default function TabListDevice({ fullList }: ITabListDevice) {
  const { translate } = useLang()
  const { darkMode } = useTheme()
  const { deviceRowsTableState, deviceRowsTableStateFiltered } =
    useDeviceTableState()
  const { enableButtonShowResumeTransferMultiple } = useListDevicesSocket()
  const tab = useHookstate<IListDeviceTab>('available')
  const searchDevice = useHookstate('')
  const searchTask = useHookstate('')
  const badgeColor = !darkMode ? '#B1B2B3' : 'rgba(255, 255, 255, 0.64)'
  const loading = useHookstate(true)
  const targetUrl = useHookstate('/device/available')
  const rowsSelected = useHookstate<readonly IListDeviceSocket[]>([])
  const enableCheckbox = useHookstate(false)
  const enableButtonShowResume = useHookstate(false)
  const openModalResultMultipleDisconnectDevice = useHookstate(false)
  const openModalResultMultipleTransferDevice = useHookstate(false)
  const openDialogTransferMultiple = useHookstate(false)
  const open = useHookstate(false)
  const LIMIT_FULL_LIST = 50
  const LIMIT = 15
  const page = useHookstate(0)
  const limit = useHookstate(fullList ? LIMIT_FULL_LIST : LIMIT)
  const devicesQuantity = useHookstate({
    available: 0,
    dispatched: 0,
    disconnected: 0,
    'not-in-service': 0,
    'on-mission': 0,
  })
  const { systemConfig } = useSystemState()

  const StyledBadge = styled(Badge)<BadgeProps>(({ theme }) => ({
    '& .MuiBadge-badge': {
      right: -8,
      top: -5,
      margin: '0 2px',
      backgroundColor: badgeColor,
    },
  }))

  useEffect(() => {
    rowsSelected.set([])
  }, [tab.get()])

  useEffect(() => {
    if (loading.get() === true) {
      Promise.all([
        getDevicesByStatus({
          targetUrl: '/device/available',
          dispatchId: null,
        }).then((response) => {
          deviceRowsTableState()['available'].set(
            response.data.map((rows) => toDomain(rows))
          )
          devicesQuantity.available.set(response.rowsCount)
        }),
        getDevicesByStatus({
          targetUrl: '/device/dispatched',
          dispatchId: null,
        }).then((response) => {
          deviceRowsTableState()['dispatched'].set(
            response.data.map((rows) => toDomain(rows))
          )
          devicesQuantity.dispatched.set(response.rowsCount)
        }),
        getDevicesByStatus({
          targetUrl: '/device/disconnected',
          dispatchId: null,
        }).then((response) => {
          deviceRowsTableState()['disconnected'].set(
            response.data.map((rows) => toDomain(rows))
          )
          devicesQuantity.disconnected.set(response.rowsCount)
        }),
        getDevicesByStatus({
          targetUrl: '/device/not-in-service',
          dispatchId: null,
        }).then((response) => {
          deviceRowsTableState()['not-in-service'].set(
            response.data.map((rows) => toDomain(rows))
          )
          devicesQuantity['not-in-service'].set(response.rowsCount)
        }),
        getDevicesByStatus({
          targetUrl: '/device/on-mission',
          dispatchId: null,
        }).then((response) => {
          deviceRowsTableState()['on-mission'].set(
            response.data.map((rows) => toDomain(rows))
          )
          devicesQuantity['on-mission'].set(response.rowsCount)
        }),
      ]).finally(() => loading.set(false))
    }
  }, [loading.get()])

  const handleTabDevice = (
    event: React.SyntheticEvent,
    value: IListDeviceTab
  ) => {
    targetUrl.set(`/device/${value}`)
    tab.set(value)
  }

  const handleCheckBox = () => {
    enableButtonShowResumeTransferMultiple.set(false)
    return enableCheckbox.set(!enableCheckbox.get())
  }

  const handleClickOpen = () => {
    switch (tab.get()) {
      case 'available':
        open.set(true)
        break
      case 'disconnected':
        openDialogTransferMultiple.set(true)
        break
    }
  }

  const clickOutModalClose = (event, reason) => {
    if (reason !== 'backdropClick') {
      open.set(false)
    }
  }

  const clickOutModalCloseTransferMultiple = (event, reason) => {
    if (reason !== 'backdropClick') {
      openDialogTransferMultiple.set(false)
    }
  }

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <InternalContent
        title={`${translate('Devices')}`}
        bold={true}
        actions={
          <Grid container justifyContent="flex-end">
            <IconButton
              title={translate('Update')}
              size="large"
              color="secondary"
              aria-label="add an alarm"
              disabled={loading.get()}
              onClick={() => {
                loading.set(true)
                searchDevice.set('')
                searchTask.set('')
              }}
            >
              <Refresh />
            </IconButton>
            {window.location.pathname !== '/window-support' && (
              <NotificationsCentral />
            )}
          </Grid>
        }
      >
        <Grid container>
          <Grid item xs={12} marginBottom={2}>
            <DeviceFilters
              clearFilters={() => {
                searchDevice.set('')
                searchTask.set('')
              }}
              searchDevice={searchDevice}
              searchTask={searchTask}
              tab={tab.get()}
            />
            {fullList && (
              <ActionsMultipleDeviceButton
                tab={tab.get()}
                rowsSelected={rowsSelected.get()}
                enableButtonShowResume={enableButtonShowResume.get()}
                enableCheckbox={enableCheckbox.get()}
                openModalResultMultipleDisconnectDevice={
                  openModalResultMultipleDisconnectDevice
                }
                openModalResultMultipleTransferDevice={
                  openModalResultMultipleTransferDevice
                }
                openDialogTransferMultiple={openDialogTransferMultiple}
                handleCheckBox={handleCheckBox}
                handleClickOpen={handleClickOpen}
              />
            )}
            <Divider />
          </Grid>
          <Grid item xs={12}>
            <TabContext value={tab.get()}>
              <TabList onChange={handleTabDevice}>
                <Tab
                  value="available"
                  sx={{ width: '170px' }}
                  label={
                    <StyledBadge
                      badgeContent={
                        deviceRowsTableStateFiltered().get()['available'].length
                      }
                      color="info"
                    >
                      <b>{translate('Online')}</b>
                    </StyledBadge>
                  }
                />
                <Tab
                  value="dispatched"
                  sx={{ width: '170px' }}
                  label={
                    <StyledBadge
                      badgeContent={
                        deviceRowsTableStateFiltered().get()['dispatched']
                          .length
                      }
                      color="info"
                    >
                      <b>{translate('Dispatched')}</b>
                    </StyledBadge>
                  }
                />
                <Tab
                  value="disconnected"
                  sx={{ width: '170px' }}
                  label={
                    <StyledBadge
                      badgeContent={
                        deviceRowsTableStateFiltered().get()['disconnected']
                          .length
                      }
                      color="info"
                    >
                      <b>{translate('Disconnected')}</b>
                    </StyledBadge>
                  }
                />
                <Tab
                  value="not-in-service"
                  sx={{ width: '170px' }}
                  label={
                    <StyledBadge
                      badgeContent={
                        deviceRowsTableStateFiltered().get()['not-in-service']
                          .length
                      }
                      color="info"
                    >
                      <b>{translate('Not in Service')}</b>
                    </StyledBadge>
                  }
                />
                {Boolean(
                  Number(systemConfig().enableOnMissionStatusOfDevice.get())
                ) && (
                  <Tab
                    value="on-mission"
                    sx={{ width: '170px' }}
                    label={
                      <StyledBadge
                        badgeContent={
                          deviceRowsTableStateFiltered().get()['on-mission']
                            .length
                        }
                        color="info"
                      >
                        <b>{translate('On Mission')}</b>
                      </StyledBadge>
                    }
                  />
                )}
              </TabList>
              <TabPanel sx={{ padding: 0 }} value="available">
                <DeviceTableUpdateSocket
                  typeStatus={'available'}
                  loading={loading}
                  searchDevice={searchDevice}
                  searchTask={searchTask}
                  fullList={fullList}
                  limit={limit}
                  enableCheckbox={enableCheckbox.get()}
                  rowsSelected={rowsSelected}
                  rowsCount={devicesQuantity.available.get()}
                  page={page}
                  exceptColumns={[
                    'dispatch',
                    'statusChangedAt',
                    'type',
                    'subtype',
                    'outOfServiceMotive',
                    'disconnectedDate',
                    'outOfServiceDate',
                    'location',
                    'comment',
                    'deviceStatusId',
                    'onMissionDate',
                    'onMissionReason',
                    'trackingId',
                  ]}
                />
              </TabPanel>
              <TabPanel sx={{ padding: 0 }} value="dispatched">
                <DeviceTableUpdateSocket
                  typeStatus={'dispatched'}
                  loading={loading}
                  searchDevice={searchDevice}
                  searchTask={searchTask}
                  fullList={fullList}
                  limit={limit}
                  rowsCount={devicesQuantity.dispatched.get()}
                  page={page}
                  exceptColumns={[
                    'comment',
                    'outOfServiceMotive',
                    'outOfServiceDate',
                    'disconnectedDate',
                    'availableDate',
                    'onMissionDate',
                    'onMissionReason',
                    'trackingId',
                  ]}
                />
              </TabPanel>
              <TabPanel sx={{ padding: 0 }} value="disconnected">
                <DeviceTableUpdateSocket
                  typeStatus={'disconnected'}
                  loading={loading}
                  searchDevice={searchDevice}
                  searchTask={searchTask}
                  fullList={fullList}
                  limit={limit}
                  page={page}
                  rowsCount={devicesQuantity.disconnected.get()}
                  enableCheckbox={enableCheckbox.get()}
                  rowsSelected={rowsSelected}
                  exceptColumns={[
                    'dispatch',
                    'type',
                    'subtype',
                    'statusChangedAt',
                    'outOfServiceMotive',
                    'outOfServiceDate',
                    'availableDate',
                    'location',
                    'deviceStatusId',
                    'comment',
                    'notes',
                    'task',
                    'commander',
                    'phone',
                    'onMissionReason',
                    'onMissionDate',
                    'trackingId',
                  ]}
                />
              </TabPanel>
              <TabPanel sx={{ padding: 0 }} value="not-in-service">
                <DeviceTableUpdateSocket
                  typeStatus={'not-in-service'}
                  loading={loading}
                  searchDevice={searchDevice}
                  searchTask={searchTask}
                  fullList={fullList}
                  limit={limit}
                  page={page}
                  rowsCount={devicesQuantity['not-in-service'].get()}
                  exceptColumns={[
                    'dispatch',
                    'location',
                    'type',
                    'statusChangedAt',
                    'subtype',
                    'deviceStatusId',
                    'availableDate',
                    'disconnectedDate',
                    'onMissionReason',
                    'onMissionDate',
                    'trackingId',
                  ]}
                />
              </TabPanel>
              {Boolean(
                Number(systemConfig().enableOnMissionStatusOfDevice.get())
              ) && (
                <TabPanel sx={{ padding: 0 }} value="on-mission">
                  <DeviceTableUpdateSocket
                    typeStatus={'on-mission'}
                    loading={loading}
                    searchDevice={searchDevice}
                    searchTask={searchTask}
                    fullList={fullList}
                    limit={limit}
                    page={page}
                    rowsCount={devicesQuantity['on-mission'].get()}
                    exceptColumns={[
                      'dispatch',
                      'location',
                      'type',
                      'statusChangedAt',
                      'subtype',
                      'deviceStatusId',
                      'availableDate',
                      'disconnectedDate',
                      'outOfServiceDate',
                      'outOfServiceMotive',
                      'trackingId',
                    ]}
                  />
                </TabPanel>
              )}
            </TabContext>
          </Grid>
        </Grid>
      </InternalContent>
      <DialogDisconnectMultipleDevices
        openDialog={open}
        onClose={clickOutModalClose}
        rowsSelected={rowsSelected}
        openModalResultMultipleDisconnectVehicle={
          openModalResultMultipleDisconnectDevice
        }
        enableButtonShowResume={enableButtonShowResume}
        clearFilters={() => {
          searchTask.set('')
          searchDevice.set('')
        }}
      />
      <DialogTransferMultipleDevices
        openDialog={openDialogTransferMultiple}
        onClose={clickOutModalCloseTransferMultiple}
        rowsSelected={rowsSelected}
        openModalResultMultipleTransfertVehicle={
          openModalResultMultipleTransferDevice
        }
        enableButtonShowResume={enableButtonShowResumeTransferMultiple}
        clearFilters={() => {
          searchTask.set('')
          searchTask.set('')
          searchDevice.set('')
        }}
      />
    </ErrorBoundary>
  )
}
